import { getAutomaticCollectionNotifications } from 'lib/api/collectionNotifications';
import { AutomaticCollectionNotification } from 'lib/api/collectionNotifications/types';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import {
  createDateRangeFilters,
  createSelectFilter,
} from 'lib/common/GlobalSearcher/filterHelpers';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import NotificationDetails from './components/NotificationDetails';

const PAGE_SIZE = 10;

const SORT_OPTIONS = [{ value: 'sendDate', label: 'Fecha de envío' }];

const SENT_OPTIONS = [
  { value: 'false', label: 'No enviado' },
  { value: 'true', label: 'Enviado' },
];

const CollectionNotifications = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [dataTable, setDataTable] = useState<AutomaticCollectionNotification[]>(
    [],
  );
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });

  const [sent, setSent] = useState<string>('false');
  const [startSendDate, setStartSendDate] = useState<string | null>(null);
  const [endSendDate, setEndSendDate] = useState<string | null>(null);
  const [orderBy, setOrderBy] = useState<string>('sendDate');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedNotification, setSelectedNotification] =
    useState<AutomaticCollectionNotification | null>(null);
  const [openNotificationDetails, setOpenNotificationDetails] = useState(false);

  function generateQuery({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }): string {
    const parts: string[] = [];
    parts.push(`sent=${sent}`);
    if (startSendDate) parts.push(`startSendDate=${startSendDate}`);
    if (endSendDate) parts.push(`endSendDate=${endSendDate}`);
    parts.push(`orderBy=${orderBy}`);
    parts.push(`order=${order}`);
    parts.push(`page=${page}`);
    parts.push(`pageSize=${pageSize}`);
    return parts.join('&');
  }

  const fetchNotifications = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    try {
      const query = generateQuery({ page, pageSize });
      const response = await getAutomaticCollectionNotifications(
        dispatch,
        query,
      );
      if (!response) {
        return showAlert(
          dispatch,
          'error',
          'Error al cargar las notificaciones',
        );
      }
      setDataTable(response.data);
      setPagination(response.pagination);
      return response;
    } catch (error) {
      return showAlert(dispatch, 'error', 'Error al cargar las notificaciones');
    }
  };

  const triggerSearch = async () => {
    setLoading(true);
    await fetchNotifications({ page: 1, pageSize: PAGE_SIZE });
    setLoading(false);
  };

  const filters: Filter[] = [
    createSelectFilter('Enviado / No enviado', SENT_OPTIONS, sent, setSent),
    ...createDateRangeFilters({
      startDateKey: 'startSendDate',
      endDateKey: 'endSendDate',
      label: 'Fecha de envío programada',
      startValue: startSendDate,
      endValue: endSendDate,
      startSetter: setStartSendDate,
      endSetter: setEndSendDate,
    }),
  ];

  const setUp = async () => {
    setLoading(true);
    await fetchNotifications({ page: 1, pageSize: PAGE_SIZE });
    setLoading(false);
  };

  useEffect(() => {
    void setUp();
  }, []);

  const handleShowDetails = async (id: number) => {
    setOpenNotificationDetails(true);
    setSelectedNotification(
      dataTable.find((x: AutomaticCollectionNotification) => x.id === id) ||
        null,
    );
  };

  const buttonsData: CustomButtonProps[] = [
    {
      name: 'Buscar',
      color: 'primary',
      action: () => {},
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={triggerSearch}
          selectedSortBy={orderBy}
          setSelectedSortBy={setOrderBy}
          sortOptions={SORT_OPTIONS}
          sortOrder={order}
          setSortOrder={setOrder}
          dispatch={dispatch}
          disableSearch={loading}
        />
      ),
    },
  ];

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        variant="body1"
        title="Aca podras ver una lista con tus emails de cobranza programados."
        buttons={buttonsData}
      />
      <TableWithPagination
        dataHeaders={[
          { key: 'id', label: 'ID', type: 'numeric-id' },
          { key: 'templateName', label: 'Plantilla' },
          { key: 'folio', label: 'Folio' },
          { key: 'amount', label: 'Monto', type: 'money' },
          { key: 'businessName', label: 'Cliente' },
          { key: 'stakeholderName', label: 'Deudor' },
          { key: 'sendDate', label: 'Fecha de envío programada', type: 'date' },
          ...(dataTable.some((x: AutomaticCollectionNotification) => x.sentAt)
            ? [{ key: 'sentAt', label: 'Enviado', type: 'date' }]
            : []),
        ]}
        data={dataTable}
        setData={setDataTable}
        searchData={fetchNotifications}
        pagination={pagination}
        setPagination={setPagination}
        rowsPerPageDefault={PAGE_SIZE}
        setLoading={setLoading}
        handleClickRow={handleShowDetails}
      />
      {selectedNotification && openNotificationDetails && (
        <NotificationDetails
          open={openNotificationDetails}
          setOpen={setOpenNotificationDetails}
          collectionNotification={selectedNotification}
        />
      )}
    </div>
  );
};

export default CollectionNotifications;
